import React, { useEffect, useState, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BsArrowBarUp } from "react-icons/bs";
import displayError from "../../utils/ErrorHandler";
import UserContext from "../../contexts/UserContext";
import authenticate from "../../utils/Authenticate";
import { formatMoney } from "../../utils/currency";

const OpenMiqaat = () => {
  const location = useLocation();
  const { miqaatDetails } = location.state || {};
  const [stock, setStock] = useState([]);
  const [mumineenCount, setMumineenCount] = useState(
    miqaatDetails?.mumineenCount || 0
  );
  const [thaalCount, setThaalCount] = useState(miqaatDetails?.thaal || 0);
  const [tempMumineenCount, setTempMumineenCount] = useState(
    miqaatDetails?.mumineenCount || 0
  );
  const [tempThaalCount, setTempThaalCount] = useState(
    miqaatDetails?.thaal || 0
  );
  const [notification, setNotification] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, setUser, navigate]);

  const getItems = useCallback(async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/stock/getStockouts`,
        { miqaat: miqaatDetails },
        { withCredentials: true }
      );

      if (response.data.success) {
        setStock(response.data.data || []);
      }
    } catch (err) {
      const errorMessage = displayError(err, navigate, setUser);
      if (errorMessage) {
        setMessage(errorMessage);
      }
    }
  }, [miqaatDetails, navigate, setUser]);

  const updateMiqaat = useCallback(async (updateObj) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/miqaat/update`,
        { updatedMiqaat: updateObj },
        { withCredentials: true }
      );

      if (response.data.success) {
        if (response.data.data === "Miqaat updated successfully !") {
          setNotification("Miqaat updated successfully!");
          setMumineenCount(updateObj.mumineenCount);
          setThaalCount(updateObj.thaal);
          setTimeout(() => setNotification(""), 3000);
        }
      }
    } catch (error) {
      console.error("Error updating miqaat:", error);
    }
  }, []);

  useEffect(() => {
    getItems();
  }, [getItems]);

  const handleOut = useCallback(() => {
    navigate("/zc/return-item-from-miqaat", { state: { miqaatDetails } });
  }, [navigate, miqaatDetails]);

  const handleSave = useCallback(() => {
    const miqaatDetailsObject = {
      _id: miqaatDetails._id,
      thaal: parseInt(tempThaalCount, 10),
      mumineenCount: parseInt(tempMumineenCount, 10),
    };

    updateMiqaat(miqaatDetailsObject);
    setIsEditing(false);
  }, [miqaatDetails, tempThaalCount, tempMumineenCount, updateMiqaat]);

  const handleMumineenCountChange = (e) => {
    setTempMumineenCount(e.target.value);
  };

  const handleThaalCountChange = (e) => {
    setTempThaalCount(e.target.value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      weekday: "short",
    };
    return date.toLocaleDateString("en-GB", options);
  };

  const totalCost = stock.reduce((acc, stockItem) => {
    const itemTotal = (stockItem.items || []).reduce((sum, item) => {
      const qty = parseFloat(item.quantity || 0);
      const price = parseFloat(item.item?.basePrice || 0);
      return sum + qty * price;
    }, 0);

    return acc + itemTotal;
  }, 0);

  return (
    <>
      <div className="bg-lime-900 flex items-center justify-between flex-col w-full px-4 text-lime-100 py-4 fixed z-10">
        <div className="flex justify-between items-center w-full">
          <button
            className="flex justify-start items-center text-white space-x-2"
            onClick={() => navigate("/zc/view-miqaats")}
            type="button"
          >
            <span className="text-xl md:text-base">←</span>
          </button>

          <h4 className="py-2 text-lg font-semibold text-center flex-grow mb-2 text-white">
            {miqaatDetails?.name}
          </h4>
        </div>

        {message ? (
          <div
            className="bg-red-100 border border-red-300 text-red-500 px-4 py-3 mx-0 rounded relative w-full"
            role="alert"
          >
            <span className="block sm:inline">{message}</span>
            <span className="absolute top-0 bottom-0 right-0 px-4 py-3"></span>
          </div>
        ) : null}

        <div className="flex w-full justify-center">
          <p className="text-md px-2">
            <strong>Thaal:</strong>{" "}
            <span className="font-semibold">{thaalCount}</span>
          </p>
          <p className="text-md px-2">
            <strong>Mumineen Count:</strong>{" "}
            <span className="font-semibold">{mumineenCount}</span>
          </p>
        </div>
      </div>

      <div className="max-w-md mx-auto">
        <div className="p-4 space-y-4 pt-[140px]">
          {notification && (
            <div className="bg-green-100 text-green-800 p-4 rounded">
              {notification}
            </div>
          )}

          {isEditing && (
            <div className="rounded shadow border border-gray-200 w-full overflow-hidden">
              <div className="p-4 bg-white">
                <div className="text-gray-500 text-sm flex justify-end space-x-2 mb-3">
                  <span>Updated On:</span>
                  <span className="font-semibold">
                    {formatDate(miqaatDetails?.updatedAt)}
                  </span>
                  <span>
                    {new Date(miqaatDetails?.updatedAt).getHours()}:
                    {String(
                      new Date(miqaatDetails?.updatedAt).getMinutes()
                    ).padStart(2, "0")}
                  </span>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="mumineenCount"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Mumineen Count
                  </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    id="mumineenCount"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    value={tempMumineenCount}
                    onChange={handleMumineenCountChange}
                  />
                </div>

                <div>
                  <label
                    htmlFor="thaalCount"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Thaal Count
                  </label>
                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    id="thaalCount"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    value={tempThaalCount}
                    onChange={handleThaalCountChange}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleSave}
                  className="mt-4 bg-fmb text-white text-sm px-4 py-2 rounded-lg shadow-md"
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {stock.length > 0 ? (
            <div className="space-y-4">
              {stock.map((stockItem) => (
                <div
                  key={stockItem._id}
                  className="rounded shadow border border-gray-200 w-full overflow-hidden bg-white"
                >
                  <div className="p-4">
                    {stockItem?.remarks?.trim() && (
                      <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-yellow-700 mb-1">
                          Remarks
                        </p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {stockItem.remarks}
                        </p>
                      </div>
                    )}

                    <ul>
                      {(stockItem.items || []).map((itemDetails, index) => (
                        <li key={itemDetails._id || index}>
                          <div className="flex justify-between items-start py-2">
                            <div className="flex-grow">
                              <h1 className="font-bold text-[16px] text-yellow-600">
                                {itemDetails.item?.name}
                              </h1>
                              <p className="text-gray-500 text-sm">
                                {itemDetails.item?.category}
                              </p>
                            </div>

                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-700">
                                {itemDetails.quantity}{" "}
                                <span className="text-sm text-gray-500">
                                  {itemDetails.item?.unit}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatMoney(user, itemDetails.item?.basePrice || 0)} /{" "}
                                {itemDetails.item?.unit}
                              </div>
                            </div>
                          </div>

                          {index < (stockItem.items || []).length - 1 && (
                            <hr className="my-2" />
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}

              <div className="mt-6 bg-white p-4 shadow rounded flex justify-between items-center">
                <h2 className="text-md font-semibold text-gray-800">
                  Total Cost:
                </h2>
                <p className="text-lg text-fmb font-bold">
                  {formatMoney(user, totalCost)}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-100 text-yellow-800 p-4 rounded">
              <p>No items to show for this Miqaat.</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full justify-center items-center mb-4 fixed bottom-16">
        <button
          type="button"
          onClick={handleOut}
          className="bg-fmb-light text-white text-sm px-4 py-2 rounded-lg shadow-md"
        >
          <div className="flex flex-row space-x-2 items-center">
            <BsArrowBarUp />
            <span>Return Item to Inventory</span>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="bg-fmb-light text-white text-sm px-4 py-2 rounded-lg ml-3 shadow-md"
        >
          <div className="flex flex-row space-x-2">
            <span>Update Miqaat</span>
          </div>
        </button>
      </div>
    </>
  );
};

export default OpenMiqaat;
